/**
 * O que faz: oferece seleção acessível e arrastar/soltar de um PDF com destaque visual.
 * Onde usa: editor de PDF da Base de conhecimento.
 * Conexões: input nativo, tokens visuais e processamento já existente via aoSelecionar.
 * Tabelas: Nenhuma; a área somente entrega o arquivo para revisão.
 * Data: 30/09/2026.
 */
import { useRef, useState } from 'react';
import { FileText, Loader2, Upload } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { LIMITE_PDF_BYTES } from '../../../supabase/functions/_shared/clinic-knowledge';

export function AreaUploadPdfConhecimento({ ocupado, processando, nomeArquivo, aoSelecionar }: {
  ocupado: boolean;
  processando: boolean;
  nomeArquivo: string | null;
  aoSelecionar: (arquivo: File) => void;
}) {
  const campoArquivo = useRef<HTMLInputElement>(null);
  const profundidadeArraste = useRef(0);
  const [arrastando, setArrastando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const selecionar = (arquivos: FileList | null) => {
    // O processamento em andamento não pode ser substituído por outro arquivo.
    if (ocupado || !arquivos?.length) return;
    // Um único documento mantém o texto extraído e a fonte privada ligados ao mesmo rascunho.
    if (arquivos.length !== 1) { setErro('Envie apenas um PDF por vez.'); return; }
    const arquivo = arquivos[0];
    // O seletor accept não filtra drag and drop; o servidor também verifica formato e conteúdo.
    if (arquivo.type !== 'application/pdf') { setErro('Selecione um arquivo PDF.'); return; }
    // Recusa arquivos vazios ou grandes antes de transferir; mantém o limite atual do servidor.
    if (!arquivo.size || arquivo.size > LIMITE_PDF_BYTES) { setErro('Envie um PDF de até 10 MB.'); return; }
    setErro(null);
    aoSelecionar(arquivo);
  };

  return <div className="space-y-2">
    <Label htmlFor="arquivo-conhecimento">Documento PDF</Label>
    {/* O botão nativo abre o seletor por clique, Enter ou Espaço; o input não duplica a parada de Tab. */}
    <input ref={campoArquivo} id="arquivo-conhecimento" className="sr-only" tabIndex={-1}
      type="file" accept="application/pdf,.pdf" disabled={ocupado}
      onChange={(evento) => { selecionar(evento.target.files); evento.target.value = ''; }} />
    <button type="button" disabled={ocupado} aria-label="Selecionar ou arrastar documento PDF"
      aria-describedby="orientacao-pdf-conhecimento" data-arrastando={arrastando && !ocupado}
      onClick={() => campoArquivo.current?.click()}
      onDragEnter={(evento) => {
        evento.preventDefault();
        // Texto arrastado não é arquivo; contar entradas evita piscar ao cruzar os ícones e textos.
        if (ocupado || !Array.from(evento.dataTransfer.types).includes('Files')) return;
        profundidadeArraste.current++;
        setArrastando(true);
      }}
      onDragLeave={(evento) => {
        evento.preventDefault();
        profundidadeArraste.current = Math.max(0, profundidadeArraste.current - 1);
        if (!profundidadeArraste.current) setArrastando(false);
      }}
      onDragOver={(evento) => {
        // Impede abrir o arquivo no navegador e habilita o drop somente quando a área está livre.
        evento.preventDefault();
        evento.dataTransfer.dropEffect = ocupado ? 'none' : 'copy';
      }}
      onDrop={(evento) => {
        evento.preventDefault();
        profundidadeArraste.current = 0;
        setArrastando(false);
        selecionar(evento.dataTransfer.files);
      }}
      className="group flex min-h-40 w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border bg-muted/30 px-4 py-6 text-center transition-colors hover:border-primary hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 data-[arrastando=true]:border-primary data-[arrastando=true]:bg-accent motion-reduce:transition-none">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
        {processando ? <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          : nomeArquivo ? <FileText className="h-6 w-6" aria-hidden="true" /> : <Upload className="h-6 w-6" aria-hidden="true" />}
      </span>
      <span className="max-w-full space-y-1">
        <span className="block text-sm font-semibold">{processando ? 'Extraindo o texto do PDF…' : arrastando && !ocupado ? 'Solte o PDF aqui' : 'Arraste seu PDF para cá'}</span>
        <span className="block text-sm text-muted-foreground">{nomeArquivo ? 'Clique ou arraste outro PDF para substituir' : 'ou clique para escolher um arquivo'}</span>
        {nomeArquivo && <span className="block break-all text-xs font-medium">{nomeArquivo}</span>}
      </span>
    </button>
    <p id="orientacao-pdf-conhecimento" className="text-xs text-muted-foreground">Até 10 MB e 50 páginas, com texto selecionável. O PDF será guardado de forma privada ao salvar.</p>
    {erro && <p role="alert" className="text-sm text-destructive">{erro}</p>}
  </div>;
}
